import { z } from 'zod';

// Day-of logistics of an in-person event (/api/events/:id/dayof, /api/events/invitees/:id/group|
// travel|travel-exceptions). Shape only: event days, stations, sessions, times and person lookups
// are checked by lib/eventDayOf.ts, which keeps its messages. The lists mirror that file.

const UUID = /^[0-9a-f-]{36}$/i;
const DAY = /^\d{4}-\d{2}-\d{2}$/;

const Id = z.string().regex(UUID, 'is not an id');
const Day = z.string().regex(DAY, 'is a date (YYYY-MM-DD)');
const SubId = z.string().min(1).max(100);
const Str = z.string().nullish();
const Flag = z.boolean().optional();
/** One item per line, or a list. */
const Items = z.union([z.string(), z.array(z.string())]).nullish();

export const EventAccommodationKind = z.enum([
  'prayer_space',
  'adjoining_room',
  'crib',
  'live_captioning',
  'dietary_indicator',
  'accessible_room',
  'mobility',
  'quiet_room',
  'lactation_room',
  'other',
]);
export type EventAccommodationKind = z.infer<typeof EventAccommodationKind>;

export const EventAccommodationWhere = z.enum(['venue', 'hotel']);
export type EventAccommodationWhere = z.infer<typeof EventAccommodationWhere>;

export const EventAccommodationStatus = z.enum(['requested', 'arranged', 'not_possible']);
export type EventAccommodationStatus = z.infer<typeof EventAccommodationStatus>;

export const EventTravelExceptionKind = z.enum([
  'extended_stay',
  'extra_airfare',
  'hotel_for_flight_only',
  'early_arrival_own_cost',
  'companion',
  'other',
]);
export type EventTravelExceptionKind = z.infer<typeof EventTravelExceptionKind>;

export const EventTravelDecision = z.enum(['pending', 'approved', 'approved_own_cost', 'declined']);
export type EventTravelDecision = z.infer<typeof EventTravelDecision>;

export const EventLegMode = z.enum(['flight', 'train', 'bus', 'car', 'other']);
export type EventLegMode = z.infer<typeof EventLegMode>;

// ---------------------------------------------------------------- params

export const EventDayOfDateParams = z.strictObject({ id: Id, date: Day });
export type EventDayOfDateParams = z.input<typeof EventDayOfDateParams>;

export const EventDayOfShiftParams = z.strictObject({ id: Id, shiftId: SubId });
export type EventDayOfShiftParams = z.input<typeof EventDayOfShiftParams>;

export const EventDayOfCodeParams = z.strictObject({ id: Id, codeId: SubId });
export type EventDayOfCodeParams = z.input<typeof EventDayOfCodeParams>;

export const EventDayOfAccommodationParams = z.strictObject({ id: Id, accId: SubId });
export type EventDayOfAccommodationParams = z.input<typeof EventDayOfAccommodationParams>;

export const EventDayOfInviteeParams = z.strictObject({ id: Id, inviteeId: Id });
export type EventDayOfInviteeParams = z.input<typeof EventDayOfInviteeParams>;

export const EventTravelExceptionParams = z.strictObject({ inviteeId: Id, exId: SubId });
export type EventTravelExceptionParams = z.input<typeof EventTravelExceptionParams>;

// ---------------------------------------------------------------- queries

/** GET /api/events/:id/dayof/staff-sheets?person= */
export const EventStaffSheetsQuery = z.strictObject({ person: z.string().optional() });
export type EventStaffSheetsQuery = z.input<typeof EventStaffSheetsQuery>;

/** GET /api/events/:id/dayof/accommodation-list?where=venue|hotel */
export const EventAccommodationListQuery = z.strictObject({
  where: z.union([EventAccommodationWhere, z.literal('')]).optional(),
});
export type EventAccommodationListQuery = z.input<typeof EventAccommodationListQuery>;

/** GET /api/events/:id/dayof/place-cards?date= (a dinner day of the event, checked by the service) */
export const EventPlaceCardsQuery = z.strictObject({ date: z.string().optional() });
export type EventPlaceCardsQuery = z.input<typeof EventPlaceCardsQuery>;

// ---------------------------------------------------------------- bodies

/** PUT /api/events/:id/dayof/stations */
export const EventStationsRequest = z.strictObject({
  stations: z.array(z.strictObject({ key: Str, name: Str, items: Items })),
});
export type EventStationsRequest = z.input<typeof EventStationsRequest>;

const ShiftPerson = z.strictObject({ userId: Str, contactId: Str, email: Str, name: Str });

const shiftShape = {
  person: ShiftPerson.nullish(),
  userId: Str,
  contactId: Str,
  email: Str,
  name: Str,
  date: Str,
  station: Str,
  sessionId: Str,
  start: Str,
  end: Str,
  notes: Str,
  items: Items,
};

/** POST /api/events/:id/dayof/shifts; the person is { userId } | { contactId } | { email } | { name }, here or in person. */
export const EventShiftCreateRequest = z.strictObject(shiftShape);
export type EventShiftCreateRequest = z.input<typeof EventShiftCreateRequest>;

/** PATCH /api/events/:id/dayof/shifts/:shiftId; only the fields sent change. */
export const EventShiftUpdateRequest = z.strictObject(shiftShape);
export type EventShiftUpdateRequest = z.input<typeof EventShiftUpdateRequest>;

/** POST /api/events/:id/dayof/groups */
export const EventGroupsRequest = z.strictObject({
  names: z.array(z.string()).nullish(),
  count: z.union([z.number(), z.string()]).nullish(),
  reassign: Flag,
  dryRun: Flag,
});
export type EventGroupsRequest = z.input<typeof EventGroupsRequest>;

/** PATCH /api/events/invitees/:inviteeId/group; null or '' clears it. */
export const EventInviteeGroupRequest = z.strictObject({ group: Str });
export type EventInviteeGroupRequest = z.input<typeof EventInviteeGroupRequest>;

/** PUT /api/events/:id/dayof/parking/garages */
export const EventGaragesRequest = z.strictObject({
  garages: z.array(z.strictObject({ key: Str, name: Str, instructions: Str })),
});
export type EventGaragesRequest = z.input<typeof EventGaragesRequest>;

/** POST /api/events/:id/dayof/parking/codes; codes is the pasted text. */
export const EventParkingCodesRequest = z.strictObject({ garage: Str, date: Str, codes: Str });
export type EventParkingCodesRequest = z.input<typeof EventParkingCodesRequest>;

/** PATCH /api/events/:id/dayof/parking/codes/:codeId; inviteeId null unassigns. */
export const EventParkingCodeUpdateRequest = z.strictObject({ inviteeId: Str, void: Flag });
export type EventParkingCodeUpdateRequest = z.input<typeof EventParkingCodeUpdateRequest>;

/** POST /api/events/:id/dayof/parking/assign */
export const EventParkingAssignRequest = z.strictObject({ dryRun: Flag });
export type EventParkingAssignRequest = z.input<typeof EventParkingAssignRequest>;

/** POST /api/events/:id/dayof/parking/sent */
export const EventParkingSentRequest = z.strictObject({ inviteeId: z.string(), undo: Flag });
export type EventParkingSentRequest = z.input<typeof EventParkingSentRequest>;

const Leg = z
  .strictObject({ date: Str, time: Str, via: Str, mode: z.union([EventLegMode, z.literal('')]).nullish(), ref: Str })
  .nullable()
  .optional();

/** PATCH /api/events/invitees/:inviteeId/travel; null removes a leg. */
export const EventTravelRequest = z.strictObject({ arrive: Leg, depart: Leg });
export type EventTravelRequest = z.input<typeof EventTravelRequest>;

const accommodationShape = {
  kind: EventAccommodationKind.optional(),
  where: z.union([EventAccommodationWhere, z.literal('')]).nullish(),
  status: z.union([EventAccommodationStatus, z.literal('')]).nullish(),
  days: z.array(z.string()).nullish(),
  detail: Str,
  outcome: Str,
};

/** POST /api/events/:id/dayof/accommodations; no inviteeId: for every attendee. */
export const EventAccommodationCreateRequest = z.strictObject({ ...accommodationShape, inviteeId: Str });
export type EventAccommodationCreateRequest = z.input<typeof EventAccommodationCreateRequest>;

/** PATCH /api/events/:id/dayof/accommodations/:accId */
export const EventAccommodationUpdateRequest = z.strictObject({ ...accommodationShape, remove: Flag });
export type EventAccommodationUpdateRequest = z.input<typeof EventAccommodationUpdateRequest>;

const exceptionShape = {
  kind: EventTravelExceptionKind.optional(),
  request: Str,
  conditions: Str,
  paymentEntryId: Str,
};

/** POST /api/events/invitees/:inviteeId/travel-exceptions (a new one is pending; a decision sent here is ignored) */
export const EventTravelExceptionCreateRequest = z.strictObject({
  ...exceptionShape,
  decision: z.string().nullish(),
  decidedOn: Str,
  decidedBy: Str,
});
export type EventTravelExceptionCreateRequest = z.input<typeof EventTravelExceptionCreateRequest>;

/** PATCH /api/events/invitees/:inviteeId/travel-exceptions/:exId */
export const EventTravelExceptionUpdateRequest = z.strictObject({
  ...exceptionShape,
  decision: EventTravelDecision.optional(),
  decidedOn: Str,
  decidedBy: Str,
});
export type EventTravelExceptionUpdateRequest = z.input<typeof EventTravelExceptionUpdateRequest>;
